import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import joseSilvaHolographic from "@/assets/jose-silva-holographic.png";
import joseSilvaHolographicWebP from "@/assets/jose-silva-holographic.webp";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const FounderShowcase = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-b from-background to-background/50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 bg-grid-white/[0.02] -z-10" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px] -z-10" />
      
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <div className="absolute inset-0 bg-primary/30 rounded-full blur-3xl" />
              <OptimizedImage
                src={joseSilvaHolographic}
                webpSrc={joseSilvaHolographicWebP}
                alt="Dr. Jose Silva - Founder, Ph.D., Author"
                className="relative z-10 w-full max-w-md mx-auto rounded-2xl"
                loading="lazy"
              />
            </motion.div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <Badge variant="outline" className="text-primary border-primary/30 px-4 py-1">
              {t('founderShowcase.badge')}
            </Badge>

            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-primary bg-clip-text text-transparent">
                {t('founderShowcase.name')}
              </h2>
              <p className="text-base text-muted-foreground font-medium">
                {t('founderShowcase.title')}
              </p>
            </div>

            <p className="text-sm text-foreground/80 leading-relaxed">
              {t('founderShowcase.description')}
            </p>

            <div className="grid grid-cols-3 gap-4 py-4">
              <div className="text-center p-4 rounded-xl bg-card/30 backdrop-blur-xl border border-white/10">
                <div className="text-2xl font-bold text-primary">15+</div>
                <div className="text-sm text-muted-foreground">{t('founderShowcase.stats.yearsExperience')}</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-card/30 backdrop-blur-xl border border-white/10">
                <div className="text-2xl font-bold text-primary">7K+</div>
                <div className="text-sm text-muted-foreground">{t('founderShowcase.stats.studentsTaught')}</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-card/30 backdrop-blur-xl border border-white/10">
                <div className="text-2xl font-bold text-primary">10+</div>
                <div className="text-sm text-muted-foreground">{t('founderShowcase.stats.coursesCreated')}</div>
              </div>
            </div>

            <Button 
              onClick={() => navigate("/about")}
              className="group gap-2"
              size="lg"
            >
              {t('founderShowcase.learnMore')}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
